/**
 * ARIA Route Guard
 *
 * Username/password login (ARIA auth Option B — see AUTH_ENTRA_ID_SSO_PLAN.md).
 * Redux's `user` slice only lives in memory, so on a fresh page load we still
 * have a token in localStorage but no user in Redux — this bootstraps that
 * state back via the `me` query before deciding whether to render or redirect.
 */

import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { useAppDispatch, useAppSelector, setUser, logout } from '../../store';
import { getAuthToken, clearAuthToken } from '../../config/apollo';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';

const ME_QUERY = gql`
  query Me {
    me {
      id
      email
      name
      role
    }
  }
`;

export function RequireAuth() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.user.isAuthenticated);
  const tokenPresent = !!getAuthToken();

  const { data, loading, error } = useQuery(ME_QUERY, {
    skip: !tokenPresent || isAuthenticated,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data?.me) {
      dispatch(
        setUser({
          id: data.me.id,
          email: data.me.email,
          name: data.me.name,
          roles: [data.me.role],
        }),
      );
    }
    if (error) {
      clearAuthToken();
      dispatch(logout());
    }
  }, [data, error, dispatch]);

  if (!tokenPresent) {
    return <Navigate to="/login" replace />;
  }

  if (!isAuthenticated) {
    if (error) {
      return <Navigate to="/login" replace />;
    }
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        {loading ? 'Loading…' : ''}
      </div>
    );
  }

  return <AuthenticatedOutlet />;
}

// Split out so useSessionTimeout's hooks only ever run once there's an actual
// session to manage — calling it unconditionally above would run its effect
// (and set up activity listeners) during the logged-out/loading states too.
function AuthenticatedOutlet() {
  useSessionTimeout();
  return <Outlet />;
}
